import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Package, LayoutDashboard } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navCls = ({ isActive }) =>
  `text-sm font-medium transition-colors ${
    isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
  }`;

export const Header = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src="/logo.webp" alt="CoreConnect" className="h-10 w-auto md:h-12" />
          <span className="hidden items-baseline gap-2 sm:flex" data-testid="header-brand-name">
            <span className="font-['IBM_Plex_Sans'] text-lg font-bold tracking-wide text-primary">CORECONNECT</span>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#dfa557]">Components</span>
          </span>
        </Link>
        <nav className="flex items-center gap-4 md:gap-6">
          <NavLink to="/" end className={navCls} data-testid="nav-products-link">
            Products
          </NavLink>
          <NavLink to="/contact" className={navCls} data-testid="nav-contact-link">
            Contact
          </NavLink>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-5 w-5 rounded-full" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  <span className="hidden max-w-28 truncate sm:inline">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-sm">
                <DropdownMenuLabel className="truncate text-xs text-muted-foreground">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/my-orders")} data-testid="menu-my-orders">
                  <Package className="mr-2 h-4 w-4" /> My Orders
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <NavLink to="/login" className={navCls} data-testid="nav-login-link">
              Sign in
            </NavLink>
          )}

          <Link
            to="/cart"
            data-testid="nav-cart-link"
            className="relative flex items-center gap-2 rounded-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-secondary"
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-xs font-bold text-accent-foreground"
              >
                {count}
              </span>
            )}
          </Link>
        </nav>
      </div>
    </header>
  );
};
